import AccountIcon from '@mui/icons-material/AccountBox'
import ArrowIcon from '@mui/icons-material/ChevronRight'
import { Box, ListItem } from '@mui/material'
import { useTranslate } from 'ra-core'
import { AppBar, LoadingIndicator, Logout, MenuItemLink, TitlePortal } from 'react-admin'
import { useLocation } from 'react-router-dom'

import { useAccounts } from '../api/accountsProvider'
import { useDocumentTitle } from '../App'
import AccountSwitch, { AccountSwitchMenuItemLink } from './AccountSwitch'
import LanguageSwitch, { LanguageSwitchMenuItemLink } from './LanguageSwitch'
import { SYSTEM_PERMISSION_READ } from '../config/permissions'
import { useCommonStyles } from '../config/theme'
import { useResourcePermissions } from '../domain/permissions'
import BankIcon from './icons/BankIcon'
import ThemeToggleButton from './ThemeToggleButton'
import UserMenu from './UserMenu'
import WhatsNewBell from './whatsNew/WhatsNewBell'
import accountConfig from '../resources/accounts/config'
import { AccountShowPage } from '../resources/accounts/show'
import opsUsersConfig from '../resources/opsUsers/config'
import payoutConfig, { PAYOUT_TITLE } from '../resources/payout/config'
import { PayoutShowScreen } from '../resources/payout/screens/show'
import { useSmallScreen } from '../utils/theme'

const userMenuItemStyle = { color: 'text.secondary' }

const CustomUserMenu = () => {
  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()

  const location = useLocation()
  const url = `${location.pathname}${location.search}${location.hash}`

  const [hasReadForAccount] = useResourcePermissions(accountConfig.name, SYSTEM_PERMISSION_READ)
  const { accounts, currentAccount } = useAccounts()
  const hasAccountsInfo = !!currentAccount
  const hasSeveralAccounts = hasAccountsInfo && accounts.length > 1

  const [hasReadForPayout] = useResourcePermissions(payoutConfig.name, SYSTEM_PERMISSION_READ)

  const [hasReadForOpsUsers] = useResourcePermissions(opsUsersConfig.name, SYSTEM_PERMISSION_READ)

  const menuItemLinkCommonProps = {
    divider: isSmallScreen,
    tooltipProps: { disableHoverListener: true },
  }
  const switchMenuItemLinkCommonProps = {
    sx: userMenuItemStyle,
    divider: true,
    to: url,
  }

  return (
    <div>
      <UserMenu>
        {hasReadForAccount && hasAccountsInfo && (
          <MenuItemLink
            {...menuItemLinkCommonProps}
            to={AccountShowPage.path}
            primaryText={isSmallScreen ? currentAccount.name : 'resources.accounts.management'}
            leftIcon={<AccountIcon />}
          />
        )}
        {isSmallScreen && !hasReadForAccount && hasAccountsInfo && (
          <ListItem sx={{ pointerEvents: 'none', py: 1.5, ...userMenuItemStyle }} divider>
            {currentAccount.name}
          </ListItem>
        )}
        {isSmallScreen && hasSeveralAccounts && <AccountSwitchMenuItemLink {...switchMenuItemLinkCommonProps} />}
        {hasReadForPayout && currentAccount && Boolean(currentAccount.is_payout_available) && (
          <MenuItemLink
            {...menuItemLinkCommonProps}
            to={PayoutShowScreen.path}
            primaryText={PAYOUT_TITLE}
            leftIcon={<BankIcon />}
          />
        )}
        {hasReadForOpsUsers && (
          <MenuItemLink
            {...menuItemLinkCommonProps}
            to={`/${opsUsersConfig.name}`}
            primaryText={translate('resources.ops-users.name', 2)}
            leftIcon={<opsUsersConfig.icon />}
          />
        )}
        {isSmallScreen && <LanguageSwitchMenuItemLink {...switchMenuItemLinkCommonProps} />}
        <Logout sx={userMenuItemStyle} />
      </UserMenu>
    </div>
  )
}

const CustomAppBar = () => {
  const commonClasses = useCommonStyles()
  const isSmallScreen = useSmallScreen()

  const { accounts, currentAccount } = useAccounts()
  const hasAccountsInfo = !!currentAccount
  const hasSingleAccount = hasAccountsInfo && accounts.length === 1
  const hasSeveralAccounts = hasAccountsInfo && accounts.length > 1

  useDocumentTitle(() => {
    const titleNode = document.getElementById('react-admin-title')
    if (!currentAccount) {
      return null
    }
    return titleNode ? currentAccount.name + ' > ' + titleNode.textContent : currentAccount.name
  })

  return hasAccountsInfo ? (
    <AppBar
      enableColorOnDark
      userMenu={<CustomUserMenu />}
      toolbar={
        <>
          <ThemeToggleButton />
          <LoadingIndicator />
          <WhatsNewBell />
        </>
      }
    >
      {isSmallScreen ? (
        <Box sx={{ flex: 1 }} />
      ) : (
        <>
          {hasSingleAccount && (
            <Box component="span" sx={{ fontSize: 20, fontWeight: 500, ...commonClasses.textWithLargeIcons }}>
              {currentAccount.name}
              <ArrowIcon />
            </Box>
          )}
          {hasSeveralAccounts && <AccountSwitch />}
          <TitlePortal
            sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', fontWeight: 500, flex: 1 }}
          />
          <LanguageSwitch />
        </>
      )}
    </AppBar>
  ) : null
}

export default CustomAppBar
